import { Duration } from "@gnu-taler/taler-util";
import { VNode, h } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { useTranslationContext } from "../../context/translation.js";
import { resolveFieldHandler, UIFormProps } from "../FormProvider.js";
import { InputLine } from "./InputLine.js";

const PATTERN = /^(?<value>[0-9]+)(?<unit>[smhDMY])$/;
type DurationUnit = "s" | "m" | "h" | "D" | "M" | "Y";
type DurationSpec = Parameters<typeof Duration.fromSpec>[0];

function parseDuration(text: string | undefined): Duration | undefined {
  if (!text?.trim()) return undefined;
  const spec: DurationSpec = {};
  for (const token of text.trim().split(/\s+/)) {
    const match = PATTERN.exec(token);
    if (!match?.groups) return undefined;
    const value = Number.parseInt(match.groups.value, 10);
    switch (match.groups.unit as DurationUnit) {
      case "s":
        spec.seconds = value;
        break;
      case "m":
        spec.minutes = value;
        break;
      case "h":
        spec.hours = value;
        break;
      case "D":
        spec.days = value;
        break;
      case "M":
        spec.months = value;
        break;
      case "Y":
        spec.years = value;
        break;
    }
  }
  return Duration.fromSpecOrUndefined(spec);
}

function formatDuration(value?: Duration): string {
  if (!value || Duration.isForever(value)) return "";
  const spec = Duration.toSpec(value);
  return [
    spec?.years ? `${spec.years}Y` : undefined,
    spec?.months ? `${spec.months}M` : undefined,
    spec?.days ? `${spec.days}D` : undefined,
    spec?.hours ? `${spec.hours}h` : undefined,
    spec?.minutes ? `${spec.minutes}m` : undefined,
    spec?.seconds ? `${spec.seconds}s` : undefined,
  ]
    .filter((part): part is string => part !== undefined)
    .join(" ");
}

export function InputDurationText(props: UIFormProps<Duration>): VNode {
  const { i18n } = useTranslationContext();
  const handler = resolveFieldHandler(props.handler, props.name);
  const forever = !!handler.value && Duration.isForever(handler.value);
  const lastFiniteValue = useRef<Duration>(Duration.fromSpec({}));

  useEffect(() => {
    if (handler.value && !Duration.isForever(handler.value)) {
      lastFiniteValue.current = handler.value;
    }
  }, [handler.value]);

  return (
    <div class="sm:col-span-6">
      <InputLine
        {...props}
        disabled={props.disabled || forever}
        type="text"
        converter={{ fromStringUI: parseDuration, toStringUI: formatDuration }}
      />
      <label class="mt-2 flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={forever}
          disabled={props.disabled}
          onChange={(event) => {
            handler.onChange(
              event.currentTarget.checked
                ? Duration.getForever()
                : lastFiniteValue.current,
            );
          }}
        />
        {i18n.str`Forever`}
      </label>
    </div>
  );
}
